import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import Modal from '../Modal'
import OnboardingInput from './OnboardingInput'
import OnboardingStepPersonalInfo from './OnboardingStepPersonalInfo'
import OnboardingStepComplete from './driver/OnboardingStepComplete'
import OnboardingStepTruckingInfo from './driver/OnboardingStepTruckingInfo'
import OnboardingStepWelcome from './driver/OnboardingStepWelcome'

interface DriverOnboardingModalProps {
  open: boolean
  onClose: () => void
}

const steps = [
  'Welcome',
  'Personal Information',
  'Trucking Information',
  'Review and Complete',
]

const DriverOnboardingModal: React.FC<DriverOnboardingModalProps> = ({
  open,
  onClose,
}) => {
  const [step, setStep] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) setStep(0)
  }, [open])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0
    }
  }, [step])

  if (!open) return null

  return (
    <Modal onClose={onClose} maxWidth={600}>
      <div ref={containerRef} className={css(styles.container)}>
        {step === 0 && <OnboardingStepWelcome onNext={() => setStep(1)} />}
        {step === 1 && (
          <OnboardingStepPersonalInfo
            onNext={() => setStep(2)}
            onBack={() => setStep(0)}
          />
        )}
        {step === 2 && (
          <OnboardingStepTruckingInfo
            onNext={() => setStep(3)}
            onBack={() => setStep(1)}
          />
        )}
        {step === 3 && <OnboardingStepComplete onClose={onClose} />}
      </div>
    </Modal>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: 'clamp(24px, 5vw, 48px)',
    width: '100%',
    maxWidth: 600,
    height: 'min(80vh, 800px)',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    background: '#F9FAFB',
    borderRadius: 12,
  },
  centered: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  truckImg: {
    width: 130,
    height: 130,
    objectFit: 'contain',
  },
  title: {
    fontSize: 'clamp(24px, 4vw, 32px)',
    fontWeight: 600,
    color: '#1F3A8A',
    margin: 'clamp(12px, 2vw, 20px) 0 clamp(6px, 1vw, 12px) 0',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 'clamp(14px, 2vw, 18px)',
    color: '#222',
    marginBottom: 'clamp(24px, 4vw, 40px)',
    textAlign: 'center',
  },
  checkList: {
    marginBottom: 'clamp(24px, 4vw, 40px)',
    maxWidth: 'min(90%, 400px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    alignSelf: 'center',
  },
  checkItem: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 'clamp(14px, 2vw, 16px)',
    marginBottom: 'clamp(8px, 1.5vw, 12px)',
    color: '#222',
    width: '100%',
  },
  checkIcon: {
    width: 24,
    height: 24,
    marginRight: 12,
    verticalAlign: 'middle',
  },
  primaryButton: {
    background: '#2563EB',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    width: '100%',
    maxWidth: 320,
    fontSize: 'clamp(14px, 2vw, 18px)',
    fontWeight: 600,
    cursor: 'pointer',
    height: 'clamp(40px, 6vw, 48px)',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '24px auto 0 auto',
    display: 'block',
  },
  secondaryButton: {
    background: '#fff',
    color: '#2563EB',
    border: '2px solid #2563EB',
    borderRadius: 12,
    padding: 'clamp(12px, 2vw, 16px) 0',
    width: '48%',
    fontSize: 'clamp(14px, 2vw, 16px)',
    fontWeight: 600,
    marginTop: 'clamp(16px, 3vw, 24px)',
    cursor: 'pointer',
  },
  buttonRow: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    gap: 16,
  },
  progressBarContainer: {
    width: '100%',
    height: 8,
    background: '#E5E7EB',
    borderRadius: 8,
    marginBottom: 24,
    marginTop: 8,
  },
  progressBar: {
    height: 8,
    borderRadius: 8,
    background: '#2563EB',
    transition: 'width 0.3s',
  },
  progressBarStep1: {
    width: '33%',
  },
  progressBarStep2: {
    width: '66%',
  },
  profilePhotoSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: 24,
    position: 'relative',
  },
  profilePhotoCircle: {
    width: 'clamp(80px, 15vw, 120px)',
    height: 'clamp(80px, 15vw, 120px)',
    borderRadius: '50%',
    background: '#F6F6F6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    position: 'relative',
  },
  profilePhotoInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addPhotoIcon: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    background: '#2563EB',
    color: '#fff',
    borderRadius: '50%',
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    border: '2px solid #fff',
    cursor: 'pointer',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    maxWidth: 400,
    margin: '0 auto 24px auto',
  },
  input: {
    padding: 'clamp(10px, 2vw, 16px)',
    borderRadius: 8,
    border: '1px solid #E5E7EB',
    fontSize: 'clamp(14px, 2vw, 16px)',
    background: '#fff',
    color: '#222',
    width: '100%',
  },
  successIcon: {
    fontSize: 80,
    color: '#22C55E',
    marginBottom: 32,
  },
  profilePhotoLabel: {
    fontSize: 10,
    fontWeight: 400,
    color: '#6B7280',
    textAlign: 'center',
    marginTop: 4,
  },
})

export default DriverOnboardingModal
